import AppConfigs from 'general/constants/AppConfigs';
import AppConstants from 'general/constants/AppConstants';
import AppRoute from 'general/constants/AppRoute';
import PreferenceKeys from 'general/constants/PreferenceKeys';
import _ from 'lodash';
import moment from 'moment';

const encodeUrlPreservingEscapes = (url) => encodeURI(url).replace(/%25([0-9A-Fa-f]{2})/g, '%$1');

// Util functions
const Utils = {
  /**
   * Lock body document scroll
   */
  lockedBodyScroll: () => {
    document.body.classList.add('body-overflow-hidden');
  },

  /**
   * Unlock body document scroll
   */
  unlockedBodyScroll: () => {
    document.body.classList.remove('body-overflow-hidden');
  },

  /**
   *
   * @param {string} text Text can copy vao clipboard
   * @param {function} callback Callback khi hoan thanh copy
   */
  copyTextToClipboard: (text, callback) => {
    navigator.clipboard?.writeText(text);
    if (_.isFunction(callback)) {
      callback();
    }
  },

  /**
   * Mo url trong tab moi
   * @param {string} url Duong dan
   */
  openInNewTab: (url) => {
    window.open(url, '_blank').focus();
  },

  /**
   * Mo url trong tab hien tai
   * @param {string} url Duong dan
   */
  openInCurrentTab: (url) => {
    window.open(url, '_self');
  },

  /**
   * Get url
   * @param {string} url Duong dan
   * @returns
   */
  getFullUrl: (url) => {
    if (!url) {
      return url;
    }
    if (url.startsWith('blob')) {
      return url;
    }
    if (!url.startsWith('http')) {
      // console.log(`${AppConfigs.env.baseUrl}${encodeURI(url)}`);
      return `${AppConfigs.env.baseUrl}${encodeUrlPreservingEscapes(url)}`;
    }
    return encodeUrlPreservingEscapes(url);
  },

  /**
   * Lay ten file
   * @param {string} url Duong dan
   * @returns
   */
  extractFilename: (url) => {
    if (!url) return null;

    // Tìm chuỗi kết thúc bằng .png
    const match = url.match(/[^/]+?\.png/);

    return match ? match[0] : null;
  },

  /**
   * Dinh dang number: 100.300,345
   * @param {number} iNumber So can dinh dang
   * @returns
   */
  formatNumber: (iNumber) => {
    return new Intl.NumberFormat('de-DE').format(iNumber);
  },

  /**
   * Set empty key to null
   * @param {object} items
   */
  setEmptyKeyToNull: (items) => {
    for (const [key, value] of Object.entries(items)) {
      if (value === '' || value === undefined) {
        items[key] = null;
      }
    }
  },

  /**
   * Remove null key
   * @param {object} items
   */
  removeNullKey: (items) => {
    for (const [key, value] of Object.entries(items)) {
      if (_.isNull(value)) {
        delete items[key];
      }
    }
  },

  /**
   * Get current window url
   * @returns
   */
  getCurrentUrl: () => {
    return window.location.href;
  },

  /**
   * Scroll div to bottom
   * @param {string} id id of element
   */
  scrollToBottom: (id) => {
    var div = document.getElementById(id);
    if (div) {
      div.scrollTop = div.scrollHeight - div.clientHeight;
    }
  },

  calculateChargeTimeInSeconds: (fromDate, endDate) => {
    if (_.isUndefined(fromDate)) return 'N/A';
    const momentFrom = moment(fromDate);
    const momentNow = endDate ? moment(endDate) : moment();
    const duration = moment.duration(momentNow.diff(momentFrom));
    const seconds = Math.round(duration.asSeconds());

    return seconds;
  },

  calculateReservedTimeInSeconds: (endDate) => {
    if (_.isUndefined(endDate) || !endDate) return 0;
    const momentNow = moment();
    const momentEnd = moment(endDate);
    if (momentEnd.isBefore(momentNow)) return 0;
    const duration = moment.duration(momentEnd.diff(momentNow));
    const seconds = Math.floor(duration.asSeconds());
    return seconds;
  },

  convertSecondsToHMS: (seconds) => {
    let hours = Math.floor(seconds / 3600);
    let remainSeconds = Math.floor(seconds % 3600);
    let minutes = Math.floor(remainSeconds / 60);
    let extraSeconds = Math.floor(remainSeconds % 60);
    hours = hours < 10 ? '0' + hours : hours;
    minutes = minutes < 10 ? '0' + minutes : minutes;
    extraSeconds = extraSeconds < 10 ? '0' + extraSeconds : extraSeconds;
    return `${hours}:${minutes}:${extraSeconds}`;
  },

  convertSecondsToMS: (seconds) => {
    let minutes = Math.floor(seconds / 60);
    let extraSeconds = Math.floor(seconds % 60);
    minutes = minutes < 10 ? '0' + minutes : minutes;
    extraSeconds = extraSeconds < 10 ? '0' + extraSeconds : extraSeconds;
    return `${minutes}:${extraSeconds}`;
  },

  convertToFontSize: (number) => {
    return `${number / 14}em`;
  },

  returnFontSize: (fontSize) => {
    return `${fontSize}px`;
  },

  formatDateTimeToString: (dateTime, format = 'DD/MM/YYYY - HH:mm:ss') => {
    return dateTime ? moment(dateTime).format(format) : '';
  },
  filterParams: (params) => {
    if (params) {
      const { selectedValue, ...filteredParams } = params;
      return Object.fromEntries(
        Object.entries(filteredParams).filter(
          ([key, value]) => value !== undefined && value !== null && value !== ''
        )
      );
    } else {
      return params;
    }
  },

  capitalizeFirstLetter(sentence) {
    if (!sentence) return '';
    return sentence
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  },

  removeSpecialCharacters(str) {
    return str.replace(/[^a-zA-Z0-9]/g, '');
  },

  getFileExt: (name) => {
    const ext = name?.split('.')?.pop();
    return ext ? ext.toUpperCase() : 'UNKNOWN';
  },

  getFileExtLower: (name) => {
    if (!name) return '';
    return name.split('?')[0].split('.').pop()?.toLowerCase() ?? '';
  },

  canPreviewFileInBrowser: (nameOrPath) => {
    const ext = Utils.getFileExtLower(nameOrPath);
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'pdf'].includes(ext);
  },

  getAccessToken: () => localStorage.getItem(PreferenceKeys.accessToken) || null,

  appendTokenToUrl: (url, token) => {
    if (!url || !token || url.includes('token=')) {
      return url;
    }
    return `${url}${url.includes('?') ? '&' : '?'}token=${token}`;
  },

  normalizeContractMediaPath: (path) => {
    if (!path || path.startsWith('http') || path.startsWith('blob')) {
      return path;
    }
    const cleanPath = path.replace(/^\//, '');
    if (cleanPath.startsWith('media/contracts/')) {
      return cleanPath;
    }
    const fileName = cleanPath.includes('/') ? _.last(cleanPath.split('/')) : cleanPath;
    return `media/contracts/${fileName}`;
  },

  buildAuthenticatedMediaUrl: (path, { useContractMedia = false, token } = {}) => {
    if (!path) {
      return '';
    }
    if (path.startsWith('blob:')) {
      return path;
    }
    if (path.startsWith('http')) {
      return Utils.appendTokenToUrl(path, token ?? Utils.getAccessToken());
    }

    const normalizedPath = useContractMedia
      ? Utils.normalizeContractMediaPath(path)
      : path.replace(/^\//, '');
    const authToken = token ?? Utils.getAccessToken();
    const fullUrl = Utils.getFullUrl(normalizedPath);
    return Utils.appendTokenToUrl(fullUrl, authToken);
  },

  openFileOrDownload: ({ url, fileName, file, useContractMedia = false, token } = {}) => {
    const resolvedName = fileName
      || (file instanceof File ? file.name : null)
      || Utils.getFileNameFromPath(url);

    if (file instanceof File) {
      if (Utils.canPreviewFileInBrowser(file.name)) {
        const objectUrl = URL.createObjectURL(file);
        Utils.openInNewTab(objectUrl);
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
      } else {
        Utils.downloadFile(file, file.name);
      }
      return;
    }

    const fullUrl = url?.startsWith('blob:')
      ? url
      : Utils.buildAuthenticatedMediaUrl(url, { useContractMedia, token });

    if (Utils.canPreviewFileInBrowser(resolvedName || url)) {
      Utils.openInNewTab(fullUrl);
      return;
    }

    const link = document.createElement('a');
    link.href = fullUrl;
    link.download = resolvedName || 'download';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  },

  convertLargeNumberToWords(number) {
    const units = ['', 'nghìn', 'triệu', 'tỷ'];
    let unitIndex = 0;
    let result = '';

    while (number >= 1000 && unitIndex < units.length - 1) {
      number = number / 1000;
      unitIndex++;
    }
    number = Math.round(number * 100) / 100;
    result = number + ' ' + units[unitIndex];
    return result.trim();
  },

  getFileNameFromPath: (path) => {
    return _.last(path?.split('/'));
  },

  getNearestMultipleOfFive: (num) => {
    if (_.isUndefined(num)) return;
    return Math.ceil(num / 5) * 5;
  },

  groupByMonth: (data, valueKey) => {
    const monthlyData = {};
    data.forEach(({ date, ...rest }) => {
      const month = date.slice(0, 7);
      if (!monthlyData[month]) {
        monthlyData[month] = 0;
      }
      monthlyData[month] += rest[valueKey] || 0;
    });

    return Object.entries(monthlyData).map(([month, total]) => ({
      month,
      [valueKey]: total,
    }));
  },

  urlToFile: async (url, filename) => {
    const res = await fetch(url);
    const blob = await res.blob();

    const file = new File([blob], filename, { type: blob.type });

    return file;
  },

  downloadFile: (file, fileName) => {
    if (!file || file.size === 0) {
      throw new Error('File rỗng hoặc không hợp lệ');
    }
    const url = window.URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  convertString: (inputString) => {
    // Remove accents and convert to lowercase
    const normalizedString = inputString
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();

    // Remove spaces and convert "đ" to "d"
    const convertedString = normalizedString.replace(/\s+/g, '').replace(/đ/g, 'd');

    return convertedString;
  },

  formatDate(dateString) {
    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Tháng bắt đầu từ 0
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${day}/${month}/${year} - ${hours}:${minutes}:${seconds}`;
  },

  calculateTotal(data, key) {
    return data.reduce((sum, item) => sum + (item[key] || 0), 0);
  },

  isAllowedToShow(user, agencyRoleList, roles = ['SALE', 'GROUP']) {
    for (let role of roles) {
      if (agencyRoleList.includes(role)) {
        return false;
      }
    }
    return Object.keys(user).length !== 0;
  },

  transformEmptyStrings(params) {
    if (params) {
      return Object.fromEntries(
        Object.entries(params).map(([key, value]) => [key, value === '' ? null : value])
      );
    } else {
      return params;
    }
  },

  isValid: (value) => {
    return value !== undefined && value !== null && value !== '';
  },

  convertSecondsToHMS: (seconds) => {
    let hours = Math.floor(seconds / 3600);
    let remainSeconds = Math.floor(seconds % 3600);
    let minutes = Math.floor(remainSeconds / 60);
    let extraSeconds = Math.floor(remainSeconds % 60);
    hours = hours < 10 ? '0' + hours : hours;
    minutes = minutes < 10 ? '0' + minutes : minutes;
    extraSeconds = extraSeconds < 10 ? '0' + extraSeconds : extraSeconds;
    return `${hours}:${minutes}:${extraSeconds}`;
  },

  convertSecondsToHMSText: (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    const hoursText = hours > 0 ? `${hours.toFixed(0)} giờ` : '';
    const minutesText = minutes > 0 ? `${minutes.toFixed(0)} phút` : '';
    const secondsText = `${remainingSeconds.toFixed(0)} giây`;

    return [hoursText, minutesText, secondsText].filter(Boolean).join(' ');
  },

  convertSecondsToHText: (seconds) => {
    const hours = (seconds / 3600).toFixed(2);
    return `${Utils.formatNumber(hours)} giờ`;
  },

  generateRandomChartData: (numPoints = 10, minValue = 0, maxValue = 100) => {
    const labels = Array.from({ length: numPoints }, (_, i) => `Label ${i + 1}`);
    const data = Array.from(
      { length: numPoints },
      () => Math.floor(Math.random() * (maxValue - minValue + 1)) + minValue
    );
    return { labels, data };
  },

  lowercaseFirstChar: (str) => {
    return str ? str.charAt(0).toLowerCase() + str.slice(1) : '';
  },

  checkDefaultFilter: (exceptItem, filter, checkDateType = 'TAT_CA_THOI_GIAN', isCheckDate = true) => {
    if (!filter) return;
    const selectedDate = filter?.selectedValue;
    const excludedKeys = Array.isArray(exceptItem) ? exceptItem : Object.keys(exceptItem || {});
    const hasNonEmptyValue = Object.entries(filter)
      .filter(([key]) => !excludedKeys.includes(key))
      .some(([, value]) => String(value).length > 0);
    const checkDate = selectedDate === checkDateType;
    return isCheckDate ? !hasNonEmptyValue && checkDate : !hasNonEmptyValue;
  },

  onEkyc: (session) => {
    window['myFunction'](session);
  },
  dashboardNavigation: (agencyRole) => {
    switch (agencyRole) {
      case AppConstants.userAgencyType.sale:
        return AppRoute.saleDashboard;
      case AppConstants.userAgencyType.investor:
        return AppRoute.investorDashboard;
      case AppConstants.userAgencyType.coinvestor:
        return AppRoute.coinvestorDashboard;
      case AppConstants.userAgencyType.landlord:
        return AppRoute.landlordDashboard;
      case AppConstants.userAgencyType.group:
        return AppRoute.groupDashboard;
      case AppConstants.userAgencyType.voucherPartner:
        return AppRoute.voucherPartnerDashboard;
      default:
        return '';
    }
  },

  getMonthDateRange: (year, month) => {
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    return {
      firstDay: Utils.formatDateTimeToString(firstDay, 'YYYY-MM-DD'),
      lastDay: Utils.formatDateTimeToString(lastDay, 'YYYY-MM-DD'),
    };
  },

  trimAll: (object) => {
    if (object && typeof object === 'object') {
      Object.keys(object).forEach((key) => {
        if (typeof object[key] === 'string') {
          object[key] = object[key].trim();
        } else if (typeof object[key] === 'object') {
          Utils.trimAll(object[key]);
        }
      });
    }
    return object;
  },

  generateDateRange: (from, to) => {
    const startDate = new Date(from);
    const endDate = new Date(to);
    if (isNaN(startDate) || isNaN(endDate)) {
      throw new Error("Ngày đầu vào không hợp lệ");
    }
    if (startDate > endDate) {
      throw new Error("Ngày bắt đầu phải trước hoặc bằng ngày kết thúc");
    }
    const dateArray = [];
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      dateArray.push(currentDate.toISOString().split("T")[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return dateArray;
  },

  parseHash: (hashStr) => {
    if (!hashStr) return {};
    return hashStr.substring(1)
      .split('&')
      .reduce((acc, pair) => {
        const [key, value] = pair.split('=');
        if (key && value) acc[key] = value;
        return acc;
      }, {});
  },
  mergeDataByDate: (dataByCar = [], dataByBike = [], dataName) => {
    const allDates = Array.from(
      new Set([...dataByCar.map(item => item.date), ...dataByBike.map(item => item.date)])
    );

    return allDates.map(date => {
      const carAmount = dataByCar.find(item => item.date === date)?.[dataName] || 0;
      const bikeAmount = dataByBike.find(item => item.date === date)?.[dataName] || 0;

      return { date, [dataName]: carAmount + bikeAmount };
    }).sort((a, b) => new Date(a.date) - new Date(b.date));
  },
  checkPermission: (permissionList = [], permissionCode) => {
    if (!permissionList || !permissionList?.length) {
      return true;
    }
    return !!permissionList.find(permission => permission?.code === permissionCode);
  },

  /**
 * Tính khoảng cách giữa hai điểm địa lý (latitude, longitude) theo công thức Haversine
 * 
 * @param {number} lat1 - Vĩ độ điểm 1 (độ)
 * @param {number} lon1 - Kinh độ điểm 1 (độ)
 * @param {number} lat2 - Vĩ độ điểm 2 (độ)
 * @param {number} lon2 - Kinh độ điểm 2 (độ)
 * @param {string} unit - Đơn vị trả về: 'km' (kilomet), 'm' (met), 'mile' (dặm Anh), 'nautical' (hải lý)
 * @returns {number} Khoảng cách theo đơn vị yêu cầu
 */
  calculateDistance: (lat1, lon1, lat2, lon2, unit = 'km') => {
    const R = 6371;
    const toRad = (deg) => deg * Math.PI / 180;

    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    let distance = R * c;

    switch (unit.toLowerCase()) {
      case 'km':
        return distance;
      case 'm':
        return distance * 1000;
      case 'mile':
        return distance * 0.621371;
      case 'nautical':
        return distance * 0.539957;
      default:
        return distance;
    }
  },

  /**
 * Mở Google Maps chỉ đường từ điểm bắt đầu đến điểm kết thúc
 * @param {Object} origin       - Điểm bắt đầu { lat: number, lng: number }
 * @param {Object} destination  - Điểm kết thúc { lat: number, lng: number }
 * @param {string} [travelMode='driving'] - Phương tiện: 'driving' | 'walking' | 'bicycling' | 'transit'
 */
  openGoogleMapsDirection: (origin, destination, travelMode = 'driving') => {
    if (!destination || typeof destination.lat !== 'number' || typeof destination.lng !== 'number') {
      console.warn('Điểm kết thúc không hợp lệ');
      return;
    }

    const mode = ['driving', 'walking', 'bicycling', 'transit'].includes(travelMode)
      ? travelMode
      : 'driving';

    let url = `https://www.google.com/maps/dir/?api=1&destination=${destination.lat},${destination.lng}&travelmode=${mode}`;

    if (origin && typeof origin.lat === 'number' && typeof origin.lng === 'number') {
      url += `&origin=${origin.lat},${origin.lng}`;
    }

    window.open(url, '_blank');
  },
};

export default Utils;
